import { AuthConfig } from 'angular-oauth2-oidc';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { environment } from '@et/environment';
import { platformType } from '@et/typings';
import type { EscrowtabOidcApp } from '../injection/escrowtab-oidc-app.token';

/**
 * Custom scheme (not the internal `capacitor://localhost` / `http://localhost`
 * WebView origins) that the OS hands back to this app after the system browser
 * completes the OIDC redirect. Registered in `Info.plist` (`CFBundleURLTypes`)
 * and `AndroidManifest.xml` (intent-filter).
 */
export const NATIVE_OIDC_CALLBACK_SCHEME = 'escrowtab://auth/callback';
export const NATIVE_OIDC_POST_LOGOUT_URI = 'escrowtab://auth/';

/**
 * iOS/Android only — Electron and web still use the in-app WKWebView/XHR flow.
 * WKWebView (and, to a lesser extent, Android's WebView) drops the Better Auth
 * session cookie set by a cross-origin `sign-in/email` XHR (third-party cookie
 * blocking), so native login must run in a system browser instead, where
 * `webapp-*.escrowtab.com` and `auth-*.escrowtab.com` share a site.
 */
function isMobileNativePlatform(): boolean {
  const platform = Capacitor.getPlatform();
  return platform === platformType.IOS || platform === platformType.ANDROID;
}

/**
 * OIDC client `issuer` must exactly match discovery `issuer` / id_token `iss`
 * (Better Auth: `metadata.issuer` → env `OIDC_ISSUER`).
 * Defaults to `betterAuth.baseUrl` when unset.
 */
export function oauthIssuerFromEnvironment(env: typeof environment): string {
  const ba = env.betterAuth as { baseUrl: string; issuer?: string };
  const url = ba.issuer ?? ba.baseUrl;
  return url.replace(/\/$/, '');
}

export function betterAuthDiscoveryUrl(env: typeof environment): string {
  const base = env.betterAuth.baseUrl.replace(/\/$/, '');
  /** Better Auth exposes discovery at `/api/auth/.well-known/...`, not `/api/auth/oauth2/.well-known/...`. */
  return `${base}/api/auth/.well-known/openid-configuration`;
}

function oidcClientId(env: typeof environment, app: EscrowtabOidcApp): string {
  return app === 'admin'
    ? env.betterAuth.clientIdAdmin
    : env.betterAuth.clientIdWeb;
}

/** Redirect URI; must match a `redirectUrls` entry on the Better Auth client. */
export function escrowtabOidcRedirectUri(): string {
  if (isMobileNativePlatform()) {
    return NATIVE_OIDC_CALLBACK_SCHEME;
  }
  if (typeof window === 'undefined') {
    return 'http://localhost:4200/auth/callback';
  }
  return `${window.location.origin}/auth/callback`;
}

/** Post-logout redirect URI; must match a `redirectUrls` entry on the Better Auth client. */
export function escrowtabOidcPostLogoutRedirectUri(): string {
  if (isMobileNativePlatform()) {
    return NATIVE_OIDC_POST_LOGOUT_URI;
  }
  if (typeof window === 'undefined') {
    return 'http://localhost:4200/';
  }
  return `${window.location.origin}/`;
}

export function buildEscrowtabAuthConfig(
  env: typeof environment,
  app: EscrowtabOidcApp,
): AuthConfig {
  return {
    issuer: oauthIssuerFromEnvironment(env),
    clientId: oidcClientId(env, app),
    redirectUri: escrowtabOidcRedirectUri(),
    postLogoutRedirectUri: escrowtabOidcPostLogoutRedirectUri(),
    responseType: 'code',
    scope: env.betterAuth.scope,
    oidc: true,
    requestAccessToken: true,
    disablePKCE: false,
    showDebugInformation: !env.production,
    strictDiscoveryDocumentValidation: false,
    skipIssuerCheck: false,
    sessionChecksEnabled: false,
    clearHashAfterLogin: true,
    /**
     * Off on purpose: the library stores the last non-callback page it booted on and
     * `history.replaceState`s back to it after the code exchange — before the router
     * boots, so `/auth/callback` never renders and AuthCallbackComponent's return-URL
     * validation is skipped. Return URLs travel in the OAuth `state` instead.
     */
    preserveRequestedRoute: false,
    /**
     * Default is `remoteOnly`, which allows http://localhost but rejects http://127.0.0.1.
     * Better Auth dev URLs often use either host; when the issuer is http, allow all http.
     */
    requireHttps: env.betterAuth.baseUrl.startsWith('https://'),
    /**
     * Default (`location.href = uri`) navigates the app's own WKWebView/WebView,
     * where the Better Auth session cookie set during login gets dropped by
     * third-party cookie blocking. Open the system browser instead so the whole
     * authorize+login round trip happens on a real `*.escrowtab.com` site.
     */
    ...(isMobileNativePlatform()
      ? {
          openUri: (uri: string) => {
            // Diagnostic for the recurring `state=undefined` seen on the
            // native callback — confirms whether the authorize request WE
            // send already has a broken `state`, or something between the
            // request and the callback (proxy, issuer) is dropping it.
            try {
              const sentState = new URL(uri).searchParams.get('state');
              console.log('[oidc] openUri: opening system browser', {
                sentState,
              });
            } catch {
              // ignore — diagnostic only
            }
            void Browser.open({ url: uri });
          },
        }
      : {}),
  };
}
